import {useTranslation} from 'react-i18next';
import {
  Control,
  UseFormWatch,
  UseFormSetValue,
  useFieldArray,
} from 'react-hook-form';
import {SVG} from '../../../../../../../constants/assetLists';
import {FormDataType} from '../../../types';
import AppTextInput from '../../../../../../../components/common/AppTextInput';
import AppFileInputButton from '../../../../../../../components/common/AppFileInputButton';
import FilePreview from '../FilePreview';

type Props = {
  index: number;
  control: Control<FormDataType>;
  watch: UseFormWatch<FormDataType>;
  setValue: UseFormSetValue<FormDataType>;
  errors: any;
};

function CheckboxOptions({index, control, errors, watch, setValue}: Props) {
  const {t} = useTranslation();

  const {fields, append, remove} = useFieldArray({
    control,
    name: `questions.${index}.options` as any,
  });

  const hasOtherOption = watch(`questions.${index}.hasOtherOption` as any);
  const otherOptionImage = watch(
    `questions.${index}.otherOptionImage` as any,
  ) as File | string | undefined;

  const handleAddOption = () => {
    const questionId = watch(`questions.${index}.id`);
    append({
      id: `${questionId}-opt-${Date.now()}`,
      title: '',
      image: undefined,
    });
  };

  const handleToggleOther = () => {
    if (!hasOtherOption) {
      setValue(`questions.${index}.hasOtherOption` as any, true, {
        shouldDirty: true,
      });
    }
  };

  const handleRemoveOther = () => {
    setValue(`questions.${index}.hasOtherOption` as any, false, {
      shouldDirty: true,
    });
    setValue(`questions.${index}.otherOptionImage` as any, undefined, {
      shouldDirty: true,
    });
  };

  const handleOptionImageChange = (
    optIndex: number,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setValue(`questions.${index}.options.${optIndex}.image` as any, file, {
        shouldDirty: true,
      });
    }
    e.target.value = '';
  };

  const handleOptionImageRemove = (optIndex: number) => {
    setValue(`questions.${index}.options.${optIndex}.image` as any, undefined, {
      shouldDirty: true,
    });
  };

  const handleOtherImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setValue(`questions.${index}.otherOptionImage` as any, file, {
        shouldDirty: true,
      });
    }
    e.target.value = '';
  };

  const handleOtherImageRemove = () => {
    setValue(`questions.${index}.otherOptionImage` as any, undefined, {
      shouldDirty: true,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Regular Options */}
      {fields.map((field, optIndex) => {
        const optionTitle = watch(
          `questions.${index}.options.${optIndex}.title` as any,
        ) as string;
        const optionImage = watch(
          `questions.${index}.options.${optIndex}.image` as any,
        ) as File | string | undefined;
        const optionError = errors?.options?.[optIndex]?.title?.message || "";
        return (
          <div key={field.id} className="flex flex-col">
            <div className="flex items-center gap-4">
              {/* Checkbox Square */}
              <div className="w-[18px] h-[18px] rounded-sm border-2 border-gray flex-shrink-0" />

              {/* Option Input */}
              <AppTextInput
                placeholder={`${t('forms.dialog.option')} ${optIndex + 1}`}
                className="flex-1 border-none"
                value={optionTitle || ''}
                error={optionError}
                onChange={e => {
                  setValue(
                    `questions.${index}.options.${optIndex}.title` as any,
                    e.target.value,
                    {shouldDirty: true},
                  );
                }}
              />

              {/* Image Upload */}
              <AppFileInputButton
                id={`q-${index}-cb-opt-${optIndex}-image`}
                icon={SVG.imageIcon}
                accept="image/*"
                onChange={e => handleOptionImageChange(optIndex, e)}
              />

              {/* Delete Option (only if more than 2 options) */}
              {fields.length > 2 && (
                <button
                  type="button"
                  onClick={() => remove(optIndex)}
                  className="p-1">
                  <img
                    src={SVG.closeDarkIcon}
                    alt="remove"
                    className="w-[16px] h-[16px] object-contain"
                  />
                </button>
              )}
            </div>

            {/* Option File Preview */}
            {optionImage && (
              <FilePreview
                file={optionImage}
                onRemove={() => handleOptionImageRemove(optIndex)}
                className="ml-[36px] mt-2"
              />
            )}
          </div>
        );
      })}

      {/* Other Option */}
      {hasOtherOption && (
        <div className="flex flex-col">
          <div className="flex items-center gap-4">
            {/* Checkbox Square */}
            <div className="w-[18px] h-[18px] rounded-sm border-2 border-gray flex-shrink-0" />

            {/* "or" label + disabled input */}
            <span className="text-[16px] text-[#1D1B20] flex-shrink-0">
              {t('forms.dialog.or')}
            </span>
            <AppTextInput
              placeholder={t('forms.dialog.other')}
              className="flex-1 border-none"
              value=""
              disabled
            />

            {/* Image Upload for Other */}
            <AppFileInputButton
              id={`q-${index}-cb-other-image`}
              icon={SVG.imageIcon}
              accept="image/*"
              onChange={handleOtherImageChange}
            />

            {/* Remove Other Option */}
            <button type="button" onClick={handleRemoveOther} className="p-1">
              <img
                src={SVG.closeDarkIcon}
                alt="remove"
                className="w-[16px] h-[16px] object-contain"
              />
            </button>
          </div>

          {/* Other Option File Preview */}
          {otherOptionImage && (
            <FilePreview
              file={otherOptionImage}
              onRemove={handleOtherImageRemove}
              className="ml-[36px] mt-2"
            />
          )}
        </div>
      )}

      {/* Add Option / Add Other */}
      <div className="flex items-center gap-4 mt-4">
        <div className="w-[18px] h-[18px] rounded-sm border-2 border-gray flex-shrink-0" />

        {!hasOtherOption ? (
          <div className="flex items-center gap-1">
            <span className={'text-[16px] text-[#1D1B20]'}>
              {t('forms.dialog.add')}
            </span>

            <button
              type="button"
              onClick={handleAddOption}
              className="text-[16px] text-linkBlue underline font-bold">
              {t('forms.dialog.option')}
            </button>

            <span className="text-[16px] text-[#1D1B20]">
              {t('forms.dialog.or')}
            </span>

            <button
              type="button"
              onClick={handleToggleOther}
              className="text-[16px] text-linkBlue underline font-bold">
              {t('forms.dialog.other')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleAddOption}
            className="text-[16px] text-linkBlue underline font-bold">
            {t('forms.dialog.add')} {t('forms.dialog.option')}
          </button>
        )}
      </div>
    </div>
  );
}

export default CheckboxOptions;
